import { useContext } from "react";
import "./SearchModal.scss";
import PropTypes from "prop-types";
import classNames from "classnames";
import Modal from "react-modal";
import { useLocation } from "react-router";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import {
	AsyncHotelOnlySearchFormContainer,
	AsyncSmartDPSearchFormContainer,
	AsyncStopoverSearchFormContainer,
} from "app/routes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import IconClose from "app/pages/.shared/IconClose";
import useReadyOnTransitionEnd from "app/utils/hooks/useReadyOnTransitionEnd";

// Filet de sécurité si transitionend ne se déclenche jamais. Valeur volontairement large et fixe,
// pas liée à la durée nominale de la transition (qui pourrait se déclencher trop tôt sur un appareil lent).
const READY_FALLBACK_MS = 2000;

const SearchForm = ({
	isStopover,
	isSDP,
	isHotelOnly,
	onSubmit,
	skipListingRedirect,
	skipLastSearchFallback,
	isLoading,
	submitButtonLabel,
	initialFocusField,
}) => {
	if (isStopover) {
		return (
			<AsyncStopoverSearchFormContainer
				onSubmit={onSubmit}
				initialFocusField={initialFocusField}
			/>
		);
	}
	if (isSDP) {
		return (
			<AsyncSmartDPSearchFormContainer
				onSubmit={onSubmit}
				skipListingRedirect={skipListingRedirect}
				skipLastSearchFallback={skipLastSearchFallback}
				isLoading={isLoading}
				submitButtonLabel={submitButtonLabel}
				initialFocusField={initialFocusField}
			/>
		);
	}
	if (isHotelOnly) {
		return (
			<AsyncHotelOnlySearchFormContainer
				onSubmit={onSubmit}
				skipListingRedirect={skipListingRedirect}
				skipLastSearchFallback={skipLastSearchFallback}
				isLoading={isLoading}
				submitButtonLabel={submitButtonLabel}
			/>
		);
	}
	return null;
};

SearchForm.propTypes = {
	isStopover: PropTypes.bool,
	isSDP: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	onSubmit: PropTypes.func,
	/** Forwarded to the SDP and hotel-only forms. */
	skipListingRedirect: PropTypes.bool,
	/** Forwarded to the SDP and hotel-only forms. */
	skipLastSearchFallback: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
	/** Forwarded to the stopover and SDP forms — jumps the form to this field on mount. */
	initialFocusField: PropTypes.string,
};

const SearchModal = ({
	title,
	isOpen,
	onClose,
	onSubmit,
	skipListingRedirect,
	skipLastSearchFallback,
	submitButtonLabel,
	isLoading,
	direction = "top",
	tunnel,
	initialFocusField,
}) => {
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const [handleOverlayRef, isReady, resetReady] = useReadyOnTransitionEnd(
		"opacity",
		READY_FALLBACK_MS
	);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const isStopover = tunnel ? tunnel === "stopover" : pathname.includes("stopover");
	const isSDP = tunnel ? tunnel === "sdp" : pathname.includes("sdp");
	const isHotelOnly = tunnel ? tunnel === "hotelonly" : pathname.includes("hotelonly");

	const canAutoFocus = !initialFocusField || isReady;

	// En sautant sur un champ (ex: lien "Choisir une autre classe"), son volet s'ouvre juste après :
	// on atténue l'ouverture de cette modale pour éviter deux animations qui s'enchaînent.
	const isJumpingToField = isMobile && Boolean(initialFocusField);

	const overlayClassName = {
		top: "search-modal__overlay search-modal__overlay--top",
		bottom: "search-modal__overlay search-modal__overlay--bottom",
	};

	const contentClassName = {
		top: "search-modal__content search-modal__content--top",
		bottom: "search-modal__content search-modal__content--bottom",
	};

	return (
		<Modal
			isOpen={isOpen}
			closeTimeoutMS={200}
			overlayRef={handleOverlayRef}
			onAfterClose={resetReady}
			onRequestClose={onClose}
			overlayClassName={overlayClassName[direction]}
			className={classNames(contentClassName[direction], {
				"search-modal__content--discreet": isJumpingToField,
			})}
		>
			{!isMobile && <HeaderContainer showMenuItems showDialPhone={false} showBackBtn />}

			{isMobile && !isJumpingToField && (
				<section className="search-modal__title">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold component="h3">
						{title}
					</Typography>

					<IconClose
						width={24}
						height={24}
						onClick={onClose}
						className="search-modal__title-close-icon"
					/>
				</section>
			)}

			{canAutoFocus && (
				<SearchForm
					isSDP={isSDP}
					isStopover={isStopover}
					isHotelOnly={isHotelOnly}
					onSubmit={onSubmit ?? onClose}
					skipListingRedirect={skipListingRedirect}
					skipLastSearchFallback={skipLastSearchFallback}
					isLoading={isLoading}
					submitButtonLabel={submitButtonLabel}
					initialFocusField={initialFocusField}
				/>
			)}
		</Modal>
	);
};

SearchModal.propTypes = {
	title: PropTypes.string.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	/** Defaults to closing the modal — pass a custom handler to do something else on submit (only reaches the SDP form). */
	onSubmit: PropTypes.func,
	/** Forwarded to the SDP and hotel-only forms. */
	skipListingRedirect: PropTypes.bool,
	/** Forwarded to the SDP and hotel-only forms. */
	skipLastSearchFallback: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
	direction: PropTypes.oneOf(["top", "bottom"]),
	/** Explicit tunnel; when omitted, the tunnel is inferred from the pathname. */
	tunnel: PropTypes.oneOf(["sdp", "stopover", "hotelonly"]),
	/** Jumps the form to this field on mount (ex: "cabin" from the "Choisir une autre classe" link). */
	initialFocusField: PropTypes.string,
};

export default SearchModal;
